// Phase 7 — Mugizh-admin Vendors, wired to real vendor CRUD.
//
// Reuses the same backend already used by the apparel-side /vendor screens
// (vendorList.tsx / createVendor.tsx): createOrUpdateVendorAction,
// getVendorListAction, getReferenceDataAction(7) for the State dropdown, and the
// shared VendorTable for the list/search/sort/pagination + row action menu
// (Edit / Active-Inactive / Delete). Add and Edit happen in a local Sheet here
// instead of navigating to /vendor/create.
import React, { useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { createOrUpdateVendorAction, getReferenceDataAction, getVendorListAction, getVendorPurchaseHistoryAction } from '../Store/actions/apparelAction';
import { deepCopyFunction } from '../Utils/helpers';
import AppConstant from '../Globals/AppConstants';
import VendorTable from '../Components/users/VendorTable';
import PurchaseHistorySheet from '../Components/users/PurchaseHistorySheet';
import { ConfirmDialog } from '../shared/confirm-dialog';
import { LoadingOverlay } from '../shared/loading-overlay';
import { Button } from 'ui/button';
import { Input } from 'ui/input';
import { Label } from 'ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from 'ui/sheet';

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const vendorTemp = {
  vendorId: "",
  name: null as any,
  description: null as any,
  emailId: null as any,
  mobileNumber: null as any,
  websiteUrl: null as any,
  contactName: null as any,
  streetArea: null as any,
  city: null as any,
  zipcode: null as any,
  stateRefId: null as any,
  isActive: 1,
  isDeleted: 0,
};

const listTemp = { searchText: "", sortBy: 0, isActive: "", limit: 10, offset: 0 };

const Vendors = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const apparelReducerState: any = useSelector((state: any) => state.ApparelReducerState);
  const { getVendorListOnLoad, getVendorListData, createOrUpdateVendorOnLoad, stateReferenceData, getVendorPurchaseHistoryData } = apparelReducerState;
  const getVendorList = getVendorListData?.getVenderList?.vendorDetails;
  const getVendorListPage = getVendorListData?.getVenderList?.page;
  const stateRefList = stateReferenceData?.getReferenceData;
  const vendorPurchaseHistory = getVendorPurchaseHistoryData?.getVendorPurchaseHistory?.rows;

  const [open, setOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [actionKey, setActionKey] = useState<"2" | "3" | null>(null);
  const [listInputs, setListInputs] = useState<any>(deepCopyFunction(listTemp));
  const [vendorInputs, setVendorInputs] = useState<any>(deepCopyFunction(vendorTemp));
  const [createOrUpdateVendorLoad, setCreateOrUpdateVendorLoad] = useState(false);
  const [errors, setErrors] = useState<any>({});
  const [purchaseHistoryOpen, setPurchaseHistoryOpen] = useState(false);
  const [vendorHistorySubject, setVendorHistorySubject] = useState<any>(null);

  const getVendorPurchaseHistoryApi = (item: any) => {
    dispatch(getVendorPurchaseHistoryAction({ vendorId: item?.vendorId }));
    setVendorHistorySubject(item);
    setPurchaseHistoryOpen(true);
  };

  const getVendorListApi = () => {
    dispatch(getVendorListAction({
      searchText: listInputs?.searchText,
      sortBy: listInputs?.sortBy,
      isActive: listInputs?.isActive !== "" ? Number(listInputs?.isActive) : null,
      // Sort id "7" is the "Outstanding (High -> Low)" option — that only
      // orders by outstandingDebt, so vendors with nothing owed still show
      // up (sorted last). Pair it with the backend's hasOutstandingDebt
      // filter so picking it actually excludes those vendors.
      hasOutstandingDebt: String(listInputs?.sortBy) === "7",
      paging: { limit: listInputs?.limit, offset: listInputs?.offset },
    }));
  };

  useEffect(() => {
    dispatch(getReferenceDataAction({ referenceGroupId: 7 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    getVendorListApi();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listInputs?.searchText, listInputs?.sortBy, listInputs?.isActive, listInputs?.offset]);

  const resetForm = () => {
    setVendorInputs(deepCopyFunction(vendorTemp));
    setErrors({});
  };

  const openAdd = () => {
    resetForm();
    setIsEdit(false);
    setOpen(true);
  };

  const openEdit = (item: any) => {
    setVendorInputs({
      vendorId: item?.vendorId,
      name: item?.vendorName,
      description: item?.description,
      emailId: item?.emailId,
      mobileNumber: item?.mobileNumber,
      websiteUrl: item?.websiteUrl,
      contactName: item?.contactName,
      streetArea: item?.streetArea,
      city: item?.city,
      zipcode: item?.zipcode,
      stateRefId: item?.stateRefId,
      isActive: item?.isActive,
      isDeleted: 0,
    });
    setErrors({});
    setIsEdit(true);
    setOpen(true);
  };

  const closeSheet = () => {
    setOpen(false);
    resetForm();
  };

  const onMenuAction = (key: string, item: any) => {
    if (key === "1") {
      openEdit(item);
      return;
    }
    setVendorInputs({
      vendorId: item?.vendorId,
      name: item?.vendorName,
      description: item?.description,
      emailId: item?.emailId,
      mobileNumber: item?.mobileNumber,
      websiteUrl: item?.websiteUrl,
      contactName: item?.contactName,
      streetArea: item?.streetArea,
      city: item?.city,
      zipcode: item?.zipcode,
      stateRefId: item?.stateRefId,
      isActive: item?.isActive,
      isDeleted: key === "3" ? 1 : 0,
    });
    setActionKey(key as "2" | "3");
    setIsModalOpen(true);
  };

  const buildPayload = (overrideIsActive?: number) => ({
    vendorId: vendorInputs?.vendorId || "",
    name: vendorInputs?.name,
    description: vendorInputs?.description,
    emailId: vendorInputs?.emailId,
    mobileNumber: vendorInputs?.mobileNumber ? String(vendorInputs.mobileNumber).trim() : "",
    websiteUrl: vendorInputs?.websiteUrl,
    contactName: vendorInputs?.contactName,
    streetArea: vendorInputs?.streetArea,
    city: vendorInputs?.city,
    zipcode: vendorInputs?.zipcode,
    stateRefId: vendorInputs?.stateRefId,
    isActive: overrideIsActive ?? vendorInputs?.isActive,
    isDeleted: vendorInputs?.isDeleted,
  });

  const createOrUpdateVendorApi = (overrideIsActive?: number) => {
    dispatch(createOrUpdateVendorAction(buildPayload(overrideIsActive)));
    setCreateOrUpdateVendorLoad(true);
  };

  // antd Form.Item rules from createVendor.tsx, ported one-to-one.
  const validate = (): boolean => {
    const next: any = {};
    const name = vendorInputs?.name;
    if (name === undefined || name === null || String(name).trim() === "") next.name = "Name is required";

    const email = vendorInputs?.emailId;
    const sanitizedEmail = email && String(email).trim().toLowerCase();
    if (sanitizedEmail && !emailPattern.test(sanitizedEmail)) next.email = "Enter the valid Email";

    const mobile = vendorInputs?.mobileNumber;
    const sanitizedMobile = mobile && String(mobile).trim().toLowerCase();
    if (sanitizedMobile && !/^[\d\s\+\-]{1,15}$/g.test(sanitizedMobile)) next.mobile = "Enter the valid Mobile Number";

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSave = () => {
    if (!validate()) return;
    createOrUpdateVendorApi();
  };

  const handleConfirm = () => {
    const overrideIsActive = actionKey === "2" ? (vendorInputs?.isActive == 1 ? 0 : 1) : vendorInputs?.isActive;
    createOrUpdateVendorApi(overrideIsActive);
  };

  useEffect(() => {
    if (createOrUpdateVendorLoad && !createOrUpdateVendorOnLoad) {
      setCreateOrUpdateVendorLoad(false);
      setIsModalOpen(false);
      setOpen(false);
      const message = actionKey === "3" ? "Vendor deleted successfully"
        : actionKey === "2" ? "Vendor status updated"
        : isEdit ? "Vendor updated successfully" : "Vendor created successfully";
      toast.success(message);
      setActionKey(null);
      resetForm();
      getVendorListApi();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createOrUpdateVendorLoad, createOrUpdateVendorOnLoad]);

  const onSearch = (value: string) => setListInputs((prev: any) => ({ ...prev, searchText: value, offset: 0 }));
  const onSortChange = (value: any) => setListInputs((prev: any) => ({ ...prev, sortBy: value, offset: 0 }));
  const onStatusFilterChange = (value: string) => setListInputs((prev: any) => ({ ...prev, isActive: value, offset: 0 }));
  const onPageChange = (page: number) => setListInputs((prev: any) => ({ ...prev, offset: ((page * 10) - 10 == 0) ? 0 : (page * 10) - 10 }));

  const confirmTitle = actionKey === "3"
    ? "Are you sure you want to delete this vendor?"
    : `Are you sure you want to ${vendorInputs?.isActive == 1 ? "deactivate" : "activate"} this vendor?`;

  return (
    <div className="mz h-full overflow-y-auto px-6 py-4">
      <VendorTable
        data={getVendorList}
        loading={getVendorListOnLoad}
        onMenuAction={onMenuAction}
        onViewHistory={getVendorPurchaseHistoryApi}
        onViewDebts={(item: any) => navigate(`/vendor-debt/${item?.vendorId}`)}
        searchValue={listInputs?.searchText}
        onSearch={onSearch}
        searchPlaceholder="Search By Name/Contact Number/Email"
        sortBy={listInputs?.sortBy}
        onSortChange={onSortChange}
        statusFilter={listInputs?.isActive}
        onStatusFilterChange={onStatusFilterChange}
        title="Vendor"
        headerActions={<Button size="sm" onClick={openAdd}><Plus className="mr-1 h-4 w-4" /> Add</Button>}
        pagination={{
          page: getVendorListPage?.currentPage || 1,
          total: getVendorListPage?.totalCount || 0,
          onChange: onPageChange,
        }}
      />

      <Sheet open={open} onOpenChange={(o) => { if (!o) closeSheet(); }}>
        <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-md">
          <SheetHeader className="border-b px-5 py-4">
            <SheetTitle>{isEdit ? 'Edit Vendor' : 'Add Vendor'}</SheetTitle>
          </SheetHeader>
          <div className="flex flex-1 flex-col gap-4 overflow-y-auto p-5">
            <p className="text-sm font-semibold text-foreground">Basic Details</p>

            <div className="space-y-1.5">
              <Label>Name <span className="text-destructive">*</span></Label>
              <Input
                value={vendorInputs?.name ?? ""}
                onChange={(e) => { setVendorInputs({ ...vendorInputs, name: e.target.value }); setErrors((er: any) => ({ ...er, name: undefined })); }}
              />
              {errors.name && <p className="text-xs text-destructive">{errors.name}</p>}
            </div>

            <div className="space-y-1.5">
              <Label>Email</Label>
              <Input
                type="email"
                value={vendorInputs?.emailId ?? ""}
                onChange={(e) => { setVendorInputs({ ...vendorInputs, emailId: e.target.value }); setErrors((er: any) => ({ ...er, email: undefined })); }}
              />
              {errors.email && <p className="text-xs text-destructive">{errors.email}</p>}
            </div>

            <div className="space-y-1.5">
              <Label>Website</Label>
              <Input value={vendorInputs?.websiteUrl ?? ""} onChange={(e) => setVendorInputs({ ...vendorInputs, websiteUrl: e.target.value })} />
            </div>

            <div className="space-y-1.5">
              <Label>Contact No.</Label>
              <Input
                value={vendorInputs?.mobileNumber ?? ""}
                onChange={(e) => { setVendorInputs({ ...vendorInputs, mobileNumber: e.target.value }); setErrors((er: any) => ({ ...er, mobile: undefined })); }}
              />
              {errors.mobile && <p className="text-xs text-destructive">{errors.mobile}</p>}
            </div>

            <div className="space-y-1.5">
              <Label>Street</Label>
              <Input value={vendorInputs?.streetArea ?? ""} onChange={(e) => setVendorInputs({ ...vendorInputs, streetArea: e.target.value })} />
            </div>

            <div className="space-y-1.5">
              <Label>City</Label>
              <Input value={vendorInputs?.city ?? ""} onChange={(e) => setVendorInputs({ ...vendorInputs, city: e.target.value })} />
            </div>

            <div className="space-y-1.5">
              <Label>Zip code</Label>
              <Input value={vendorInputs?.zipcode ?? ""} onChange={(e) => setVendorInputs({ ...vendorInputs, zipcode: e.target.value })} />
            </div>

            <div className="space-y-1.5">
              <Label>State</Label>
              <Select
                value={vendorInputs?.stateRefId != null ? String(vendorInputs.stateRefId) : undefined}
                onValueChange={(v) => setVendorInputs({ ...vendorInputs, stateRefId: Number(v) })}
              >
                <SelectTrigger><SelectValue placeholder="Select State" /></SelectTrigger>
                <SelectContent>
                  {(stateRefList || []).map((item: any, index: number) => (
                    <SelectItem key={index} value={String(item?.id)}>{item?.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label>Description</Label>
              <textarea
                className="flex min-h-[80px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                value={vendorInputs?.description ?? ""}
                onChange={(e) => setVendorInputs({ ...vendorInputs, description: e.target.value })}
              />
            </div>

            <div className="mt-auto flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={closeSheet}>Cancel</Button>
              <Button disabled={createOrUpdateVendorLoad} onClick={handleSave}>{isEdit ? 'Save' : 'Create'}</Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      <ConfirmDialog
        open={isModalOpen}
        onOpenChange={(o) => { if (!o) { setIsModalOpen(false); setActionKey(null); } }}
        title={confirmTitle}
        confirmLabel={actionKey === "3" ? AppConstant?.Delete : "Ok"}
        cancelLabel={AppConstant?.cancel}
        destructive={actionKey === "3" || (actionKey === "2" && vendorInputs?.isActive == 1)}
        loading={createOrUpdateVendorLoad}
        onConfirm={handleConfirm}
      />

      <LoadingOverlay loading={createOrUpdateVendorLoad} />

      <PurchaseHistorySheet
        open={purchaseHistoryOpen}
        onClose={() => setPurchaseHistoryOpen(false)}
        title={vendorHistorySubject?.vendorName}
        resetKey={vendorHistorySubject?.vendorId}
        historyByDate={vendorPurchaseHistory}
        getItemInvoiceId={(item: any) => item?.invoiceId}
        getItemAmount={(item: any) => Number(item?.totalAmount) || 0}
        renderItem={(item1: any, idx: number) => (
          <div key={idx} className="flex items-start justify-between gap-3 rounded-lg border p-3">
            <div className="min-w-0 truncate text-sm text-foreground">{item1?.invoiceId}</div>
            <div className="whitespace-nowrap text-sm tabular-nums">₹{Number(item1?.totalAmount ?? 0).toFixed(2)}</div>
          </div>
        )}
      />
    </div>
  );
};

export default Vendors;
